import type { ApiErrorShape } from '@metadevprofile/shared/api';
import { useGetProfileQuery } from '@metadevprofile/shared/api';
import { useEffect } from 'react';
import { getEdenErrorMessage } from '@/common/lib/get-eden-error';
import { notificationService } from '@/common/lib/notification-service';
import { useAppDispatch } from '@/common/redux/store';
import { setPreferences } from '@/profile/stores/preferences-slice';

/**
 * A missing profile (404 — `GET /profiles` throws `NotFoundError` when the row doesn't exist yet)
 * is an expected state here: a signed-in user who hasn't finished onboarding. Any other error
 * (500, network failure, ...) is NOT a confirmed absence — callers deciding whether to redirect to
 * onboarding must treat that the same as still-loading (see `isConfirmedAbsent`), or a transient
 * failure would bounce an existing user with a real profile into onboarding.
 */
export function useProfile(options?: { skip?: boolean }) {
  const dispatch = useAppDispatch();
  const { data, isLoading, error } = useGetProfileQuery(undefined, { skip: options?.skip });
  const typedError = error as ApiErrorShape | undefined;
  const isConfirmedAbsent = typedError?.status === 404;

  useEffect(() => {
    if (data) {
      // `language`/`theme` stay client-side in the slimmed preferences slice (used by
      // useThemeManager, the markdown editor, ...) — keep them synced whenever the server profile
      // is (re)loaded.
      dispatch(setPreferences({ language: data.language, theme: data.theme }));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (typedError && !isConfirmedAbsent) {
      notificationService.error(getEdenErrorMessage(typedError, 'profile:errors.loadProfileFailed'));
    }
  }, [typedError, isConfirmedAbsent]);

  return {
    profile: data ?? null,
    loading: isLoading,
    isError: !!typedError,
    isConfirmedAbsent,
  };
}
